import { useQuery, useMutation, useQueryClient } from 'react-query'

import { getLiveId } from '../../../../global/blocs/roomInfo'
import {
  createPoll,
  endPoll,
  getPolls,
  setPollOptions,
  startPoll,
  togglePollResults,
  updatePollTitle,
} from '../api/polls'
import { type Poll, pollMapper } from '../models/poll'

/** Get polls for webinar */
export function usePollsQuery() {
  return useQuery(['polls'], async (): Promise<Poll[]> => {
    const mapper = pollMapper()
    return (await getPolls(getLiveId())).map((dto) => mapper.fromDto(dto))
  })
}

export function useInvalidatePolls() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries(['polls'])
}

export function useInvalidatePollsQuery(): () => Promise<void> {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries(['polls'])
}

export function useStartPollMutation(pollId: number) {
  return useMutation({
    mutationKey: ['start-poll', pollId],
    mutationFn: () => startPoll(pollId),
  })
}

export function useEndPollMutation(pollId: number) {
  return useMutation({
    mutationKey: ['end-poll', pollId],
    mutationFn: () => endPoll(pollId),
  })
}

export function useTogglePollResultsMutation(pollId: number) {
  return useMutation({
    mutationKey: ['toggle-poll-results', pollId],
    mutationFn: (open: boolean) => togglePollResults(pollId, open),
  })
}

export function useCreatePollMutation(liveId: number) {
  return useMutation({
    mutationKey: ['create-poll'],
    mutationFn: (question: string) => createPoll(liveId, question),
  })
}

export function useUpdatePollTitleMutation() {
  return useMutation({
    mutationKey: ['update-poll-title'],
    mutationFn: async ({
      pollId,
      title,
    }: {
      pollId: number
      title: string
    }) => {
      if (!pollId) return
      await updatePollTitle(pollId, title)
    },
  })
}

export function useSetPollOptionsMutation() {
  return useMutation({
    mutationKey: ['set-poll-options'],
    mutationFn: (params: { pollId: number; options: string[] }) =>
      setPollOptions(params.pollId, params.options),
  })
}
